'use client';

import { useEffect } from 'react';

/**
 * Last-resort boundary: catches failures in the ROOT LAYOUT itself, where
 * Next.js replaces the entire document. It must render its own <html>/<body>
 * and therefore cannot rely on the app's stylesheet — everything here is
 * inline, deliberately.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const reference = error.digest ?? 'unavailable';

  useEffect(() => {
    // Fire-and-forget: the app is already down, so this must not await.
    const body = JSON.stringify({
      message: error.message?.slice(0, 500) ?? 'Global error',
      source: 'global',
      stack: error.stack?.slice(0, 4000),
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      release: process.env.NEXT_PUBLIC_APP_VERSION ?? 'dev',
    });
    try {
      navigator.sendBeacon?.('/api/errors', new Blob([body], { type: 'application/json' }));
    } catch {
      /* nothing more we can do at this point */
    }
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a0e1a',
          color: '#e5e7eb',
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 460, textAlign: 'center' }}>
          <div
            style={{
              width: 56,
              height: 56,
              margin: '0 auto 24px',
              borderRadius: 16,
              background: 'rgba(239,68,68,0.15)',
              border: '1px solid rgba(239,68,68,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
            }}
            aria-hidden="true"
          >
            ⚠
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 8px' }}>
            Glanz CRM needs a restart
          </h1>
          <p style={{ fontSize: 14, color: '#9ca3af', margin: '0 0 24px', lineHeight: 1.6 }}>
            An unexpected error stopped the application. Nothing was changed or lost — reload to
            continue from where you were.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={reset}
              style={{
                background: '#5a5fe6',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reload
            </button>
            <a
              href="/contact"
              style={{
                background: 'transparent',
                color: '#e5e7eb',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 8,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Contact support
            </a>
          </div>
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 24 }}>
            Reference: <code>{reference}</code>
          </p>
        </div>
      </body>
    </html>
  );
}
